import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * Pantry items with the linked Food's name + brand for display.
 * Sort by location, then by expiration ascending (nearest expiry first).
 */

export type PantryRow = {
  id: string;
  food_id: string;
  food_name: string;
  food_brand: string | null;
  location: string;
  qty: number;
  unit: string;
  opened: boolean;
  purchased: string;
  expires: string | null;
  staple: boolean;
  restock_priority: string | null;
  notes: string;
  lookup_source: "off" | "usda" | "photo_label" | "ai" | "manual" | null;
  nutrition_refreshed_at: string | null;
};

export function usePantry() {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery({
    queryKey: ["pantry", userId],
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async (): Promise<PantryRow[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("pantry_item")
        .select(
          "id, food_id, location, qty, unit, opened, purchased, expires, staple, restock_priority, notes, lookup_source, nutrition_refreshed_at, food:food!inner(name, brand)",
        )
        .eq("user_id", userId)
        .order("location", { ascending: true })
        .order("expires", { ascending: true, nullsFirst: false });
      if (error) throw error;
      const rows = (data ?? []) as unknown as Array<{
        id: string;
        food_id: string;
        location: string;
        qty: number;
        unit: string;
        opened: boolean;
        purchased: string;
        expires: string | null;
        staple: boolean;
        restock_priority: string | null;
        notes: string;
        lookup_source: string | null;
        nutrition_refreshed_at: string | null;
        food: { name: string; brand: string | null } | null;
      }>;
      return rows.map((r) => ({
        id: r.id,
        food_id: r.food_id,
        food_name: r.food?.name ?? "—",
        food_brand: r.food?.brand ?? null,
        location: r.location,
        qty: Number(r.qty),
        unit: r.unit,
        opened: r.opened,
        purchased: r.purchased,
        expires: r.expires,
        staple: r.staple,
        restock_priority: r.restock_priority,
        notes: r.notes,
        lookup_source: (r.lookup_source ?? null) as PantryRow["lookup_source"],
        nutrition_refreshed_at: r.nutrition_refreshed_at ?? null,
      }));
    },
  });
}
